import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { BackupsService } from '../../../../core/services/backups.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { PageEvent } from '@angular/material/paginator';
import { BackupEndStatus, WebsiteBackup } from '../../../../shared/models/backups.models';
import { Status } from '../../../../shared/models/status-label.model';
import moment from 'moment';
import { DatePickerComponent } from '../../../../shared/components/date-picker/date-picker.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RestoreBackupDialogComponent } from '../../dialogs/restore-backup-dialog/restore-backup-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { BackupDetailsDialogComponent } from '../../dialogs/backup-details-dialog/backup-details-dialog.component';
import { DateRangeValue } from '../../../../shared/components/date-picker/date-picker.models';

@Component({
  selector: 'app-backups',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    TableComponent,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    AsyncPipe,
    MatButtonModule,
    DatePickerComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './backups.component.html',
  styleUrl: './backups.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackupsComponent implements OnInit {
  private backupsService = inject(BackupsService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  filters = signal<{
    page: number;
    pageSize: number;
    searchText?: string;
    timestampFrom?: number;
    timestampTo?: number;
  }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  dateRange = new FormControl<DateRangeValue | null>(null);

  allBackupsCount = signal(0);

  backups$ = combineLatest([this.website$, this.filters$.pipe(debounceTime(300))]).pipe(
    switchMap(([website, filters]) =>
      this.refresh$.pipe(
        switchMap(() =>
          this.backupsService.searchWebsiteBackups({ ...filters, websiteId: website.id }),
        ),
      ),
    ),
    tap(({ total }) => this.allBackupsCount.set(total)),
    map(({ data }) => data ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  backupsColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: WebsiteBackup) => element.id.toString(),
    },
    {
      columnDef: 'repo',
      header: 'Repository',
      cell: (element: WebsiteBackup) => element.backup.repositoryId.toString(),
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'createdAt',
      header: 'Created At',
      cell: (element: WebsiteBackup) =>
        moment(element.backup.createdAt).format('DD.MM.YYYY, hh:mm:ss'),
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.dateRange.valueChanges.subscribe((value: DateRangeValue | null) => {
      if (value) {
        const start = value.start?.valueOf();
        const end = value.end?.valueOf();

        if (start && end)
          this.filters.update((f) => ({ ...f, timestampFrom: start, timestampTo: end }));
      }
    });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, searchText: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  clearFilters(): void {
    this.filters.update((f) => ({ ...f, timestampFrom: undefined, timestampTo: undefined }));
    this.dateRange.reset();
  }

  restoreBackup(backup: WebsiteBackup): void {
    this.dialog
      .open(RestoreBackupDialogComponent, {
        data: {
          subTitle: backup.domain,
          backupId: backup.backupId,
          server: this.websiteData().server?.hostname,
          createdAt: moment(backup.backup.createdAt).format('DD.MM.YYYY'),
          websiteBackupId: backup.websiteBackupTargets[0].websiteBackupId,
          backupTargets: backup.websiteBackupTargets,
          config: this.parseNestedJson(backup.configJson),
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.backupsService.restoreWebsiteBackups(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Backup restore started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  showDetails(backup: WebsiteBackup): void {
    this.dialog.open(BackupDetailsDialogComponent, {
      data: {
        subTitle: this.website().domain,
        backupTargets: backup.websiteBackupTargets,
        config: this.parseNestedJson(backup.configJson),
      },
    });
  }

  toStatus(key: BackupEndStatus): Status {
    switch (key) {
      case BackupEndStatus.SUCCESS:
        return Status.ACTIVE;
      case BackupEndStatus.PARTIAL:
        return Status.PARTIAL;
      case BackupEndStatus.PENDING:
        return Status.PENDING;
      case BackupEndStatus.SCHEDULED:
        return Status.IN_PROGRESS;
      default:
        return Status.ERROR;
    }
  }

  private parseNestedJson<T>(value: T): T {
    if (typeof value === 'string') {
      try {
        return this.parseNestedJson(JSON.parse(value));
      } catch {
        return value;
      }
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.parseNestedJson(item)) as T;
    }

    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, child]) => [key, this.parseNestedJson(child)]),
      ) as T;
    }

    return value;
  }
}
